import React, { useEffect, useState } from 'react';
import {
  Loader2, Plus, Download, Trash2, RefreshCw, Eye, Send, CheckCircle2, Ban, Landmark,
} from 'lucide-react';
import FintsSubmitDialog from './FintsSubmitDialog';
import api from '@/lib/axios';
import { formatDeNumber } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import DateInput from '@/components/ui/date-input';
import { fruehesterBankarbeitstag } from '@/lib/bankDay';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const emptyItem = () => ({
  recipient_name: '',
  recipient_iban: '',
  recipient_bic: '',
  amount: '',
  remittance_info: '',
});

const fmtMoney = (n) => `${formatDeNumber(n)} €`;
const fmtDate = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10);
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
};

const STATUS = {
  draft: { label: 'Entwurf', className: 'border-slate-200 bg-slate-50 text-slate-600' },
  exported: { label: 'Exportiert', className: 'border-sky-200 bg-sky-50 text-sky-700' },
  submitted: { label: 'Eingereicht', className: 'border-amber-200 bg-amber-50 text-amber-700' },
  completed: { label: 'Abgeschlossen', className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  cancelled: { label: 'Storniert', className: 'border-rose-200 bg-rose-50 text-rose-600' },
};

export default function SepaTransfersTab({ creditorConfigured }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fintsTarget, setFintsTarget] = useState(null);
  const [form, setForm] = useState({
    execution_date: new Date().toISOString().slice(0, 10),
    notes: '',
    items: [emptyItem()],
  });

  const load = () => {
    setLoading(true);
    api.get('/sepa/transfers')
      .then((r) => setBatches(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || 'Laden fehlgeschlagen'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => {
    setForm({
      execution_date: new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10),
      notes: '',
      items: [emptyItem()],
    });
    setShowCreate(true);
  };

  const updateItem = (idx, patch) => {
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  };

  const createBatch = async () => {
    const items = form.items
      .map((it) => ({
        recipient_name: it.recipient_name.trim(),
        recipient_iban: it.recipient_iban.trim(),
        recipient_bic: it.recipient_bic.trim() || null,
        amount: parseFloat(String(it.amount).replace(',', '.')),
        remittance_info: it.remittance_info.trim() || null,
      }))
      .filter((it) => it.recipient_name || it.recipient_iban || it.amount);

    if (!items.length) {
      toast.error('Mindestens eine Überweisung eingeben');
      return;
    }
    for (const it of items) {
      if (!it.recipient_name || !it.recipient_iban || !(it.amount > 0)) {
        toast.error('Jede Zeile braucht Empfänger, IBAN und Betrag > 0');
        return;
      }
    }

    setSaving(true);
    try {
      const r = await api.post('/sepa/transfers', {
        execution_date: form.execution_date,
        notes: form.notes || null,
        items,
      });
      toast.success(`Batch #${r.data.batch_number} angelegt`);
      setShowCreate(false);
      load();
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const exportBatch = async (id) => {
    try {
      const r = await api.post(`/sepa/transfers/${id}/export`);
      toast.success(r.data.message || 'XML erstellt');
      await downloadBatch(id, r.data.filename);
      load();
      if (detail?.id === id) {
        const d = await api.get(`/sepa/transfers/${id}`);
        setDetail(d.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export fehlgeschlagen');
    }
  };

  const downloadBatch = async (id, filename) => {
    try {
      const r = await api.get(`/sepa/transfers/${id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'ueberweisungen.xml';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download fehlgeschlagen');
    }
  };

  const mark = async (id, action) => {
    try {
      await api.post(`/sepa/transfers/${id}/${action}`);
      toast.success('Status aktualisiert');
      load();
      if (detail?.id === id) {
        const d = await api.get(`/sepa/transfers/${id}`);
        setDetail(d.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Batch stornieren bzw. löschen?')) return;
    try {
      await api.delete(`/sepa/transfers/${id}`);
      toast.success('Erledigt');
      if (detail?.id === id) setDetail(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/sepa/transfers/${id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Detail laden fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4" data-testid="sepa-transfers-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500">
          Allgemeine ausgehende Überweisungen. Die Datei kann heruntergeladen oder direkt über HBCI/FinTS an die Bank gesendet werden.
          Auftraggeber ist das hinterlegte Gläubiger-Konto.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" /> Aktualisieren</Button>
          <Button size="sm" onClick={openCreate} disabled={!creditorConfigured} data-testid="new-transfer-btn">
            <Plus className="w-4 h-4 mr-1" /> Neue Überweisung
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : batches.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400 italic" data-testid="transfers-empty">
              Noch keine Überweisungsbatches. Legen Sie Empfänger, Betrag und Verwendungszweck an und exportieren Sie die XML.
            </div>
          ) : (
            <Table data-testid="transfers-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nr.</TableHead>
                  <TableHead>Ausführung</TableHead>
                  <TableHead className="text-right">Posten</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((b) => {
                  const st = STATUS[b.status] || STATUS.draft;
                  return (
                    <TableRow key={b.id} data-testid={`transfer-row-${b.batch_number}`}>
                      <TableCell className="font-mono text-xs">#{b.batch_number}</TableCell>
                      <TableCell>{fmtDate(b.execution_date)}</TableCell>
                      <TableCell className="text-right">{b.item_count}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">{fmtMoney(b.total_amount)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${st.className}`}>{st.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-0.5 justify-end">
                          <Button size="sm" variant="ghost" onClick={() => openDetail(b.id)} title="Details">
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          {b.status === 'draft' && (
                            <Button size="sm" variant="ghost" onClick={() => exportBatch(b.id)} title="XML exportieren">
                              <Download className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {b.xml_filename && (
                            <Button size="sm" variant="ghost" onClick={() => downloadBatch(b.id, b.xml_filename)} title="XML laden">
                              <Download className="w-3.5 h-3.5 text-sky-600" />
                            </Button>
                          )}
                          {b.status === 'exported' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setFintsTarget({ type: 'transfer', id: b.id })}
                              title="An die Bank senden"
                              data-testid={`transfer-fints-${b.batch_number}`}
                            >
                              <Landmark className="w-3.5 h-3.5 text-emerald-600" />
                            </Button>
                          )}
                          {b.status === 'exported' && (
                            <Button size="sm" variant="ghost" onClick={() => mark(b.id, 'mark-submitted')} title="Als eingereicht markieren">
                              <Send className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {(b.status === 'exported' || b.status === 'submitted') && (
                            <Button size="sm" variant="ghost" onClick={() => mark(b.id, 'mark-completed')} title="Abschließen">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            </Button>
                          )}
                          {b.status !== 'completed' && (
                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(b.id)} title="Stornieren/Löschen">
                              {b.status === 'draft' ? <Trash2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Neue Sammelüberweisung</DialogTitle>
            <DialogDescription>
              Beliebige Empfänger — Erstattung, Lieferant, einmalige Auszahlung. Danach direkt an die Bank senden oder als XML herunterladen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Ausführungsdatum *</Label>
                <DateInput
                  value={form.execution_date}
                  onChange={(e) => setForm({ ...form, execution_date: e.target.value })}
                  data-testid="transfer-exec-date"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auch ein Datum in einem vergangenen Monat ist möglich, zum Beispiel der 15.05.
                </p>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  data-testid="transfer-sofort"
                  onClick={() => setForm((f) => ({ ...f, execution_date: fruehesterBankarbeitstag() }))}
                >
                  Sofort
                </Button>
              </div>
              <div>
                <Label>Notiz</Label>
                <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Empfänger</Label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }))}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Zeile
                </Button>
              </div>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end border border-slate-100 rounded-lg p-2">
                  <div className="col-span-12 sm:col-span-4">
                    <Label className="text-xs">Name *</Label>
                    <Input
                      value={it.recipient_name}
                      onChange={(e) => updateItem(idx, { recipient_name: e.target.value })}
                      data-testid={`transfer-name-${idx}`}
                    />
                  </div>
                  <div className="col-span-8 sm:col-span-3">
                    <Label className="text-xs">IBAN *</Label>
                    <Input
                      value={it.recipient_iban}
                      onChange={(e) => updateItem(idx, { recipient_iban: e.target.value })}
                      data-testid={`transfer-iban-${idx}`}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-xs">BIC</Label>
                    <Input
                      value={it.recipient_bic}
                      onChange={(e) => updateItem(idx, { recipient_bic: e.target.value })}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2">
                    <Label className="text-xs">Betrag *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={it.amount}
                      onChange={(e) => updateItem(idx, { amount: e.target.value })}
                      data-testid={`transfer-amount-${idx}`}
                    />
                  </div>
                  <div className="col-span-8 sm:col-span-10">
                    <Label className="text-xs">Verwendungszweck</Label>
                    <Input
                      value={it.remittance_info}
                      onChange={(e) => updateItem(idx, { remittance_info: e.target.value })}
                      maxLength={140}
                    />
                  </div>
                  <div className="col-span-4 sm:col-span-2 flex justify-end">
                    {form.items.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        onClick={() => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Abbrechen</Button>
            <Button onClick={createBatch} disabled={saving} data-testid="transfer-create-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>Überweisung #{detail.batch_number}</DialogTitle>
                <DialogDescription>
                  Ausführung {fmtDate(detail.execution_date)} · {fmtMoney(detail.total_amount)} ·{' '}
                  {(STATUS[detail.status] || STATUS.draft).label}
                </DialogDescription>
              </DialogHeader>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empfänger</TableHead>
                    <TableHead>IBAN</TableHead>
                    <TableHead className="text-right">Betrag</TableHead>
                    <TableHead>Zweck</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(detail.items || []).map((it) => (
                    <TableRow key={it.id}>
                      <TableCell>{it.recipient_name}</TableCell>
                      <TableCell className="font-mono text-xs">{it.recipient_iban}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtMoney(it.amount)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{it.remittance_info || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <DialogFooter className="gap-2">
                {detail.status === 'draft' && (
                  <Button onClick={() => exportBatch(detail.id)} data-testid="transfer-export-btn">
                    <Download className="w-4 h-4 mr-1" /> XML exportieren
                  </Button>
                )}
                {detail.xml_filename && (
                  <Button variant="outline" onClick={() => downloadBatch(detail.id, detail.xml_filename)}>
                    <Download className="w-4 h-4 mr-1" /> Download
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <FintsSubmitDialog
        open={!!fintsTarget}
        onOpenChange={(weiter) => { if (!weiter) setFintsTarget(null); }}
        targetType={fintsTarget?.type}
        targetId={fintsTarget?.id}
        onSubmitted={load}
      />
    </div>
  );
}
